import { useState } from 'react';
import { completeOnboarding, createAlpacaAccount } from '../lib/cerebro';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';

// 7 preguntas del onboarding (alineadas con el backend)
const QUESTIONS = [
  {
    id: 'monthlyInvestment',
    title: 'Tu capacidad de inversion',
    question: 'Cuanto puedes invertir al mes?',
    options: [
      { value: 'bajo', label: 'Menos de $100', desc: 'Empezar con poco esta bien' },
      { value: 'medio', label: '$100 - $500', desc: 'Inversion constante' },
      { value: 'alto', label: '$500 - $2,000', desc: 'Crecimiento acelerado' },
      { value: 'muy_alto', label: 'Mas de $2,000', desc: 'Inversion seria' },
    ],
  },
  {
    id: 'reactionToDrop',
    title: 'Tu temperamento',
    question: 'Tu inversion baja 25% en una semana. Que haces?',
    options: [
      { value: 'sell', label: 'Vendo para no perder mas', desc: 'Prefiero proteger lo que tengo' },
      { value: 'hold', label: 'No hago nada, espero', desc: 'Tengo paciencia' },
      { value: 'buy_more', label: 'Compro mas! Esta en oferta', desc: 'Las caidas son oportunidades' },
    ],
  },
  {
    id: 'timeHorizon',
    title: 'Tu horizonte de tiempo',
    question: 'Cuando necesitas esta plata?',
    options: [
      { value: 'short', label: 'Menos de 2 anos', desc: 'Corto plazo' },
      { value: 'medium', label: '2 a 5 anos', desc: 'Mediano plazo' },
      { value: 'long', label: '5 a 10 anos', desc: 'Largo plazo' },
      { value: 'unlimited', label: 'No la necesito', desc: 'Es plata que puedo dejar crecer' },
    ],
  },
  {
    id: 'excitement',
    title: 'Que te emociona mas?',
    question: 'Que tipo de inversion te atrae?',
    options: [
      { value: 'safe_growth', label: 'Crecimiento seguro', desc: 'Dormir tranquilo sabiendo que crece' },
      { value: 'world_changers', label: 'Empresas que cambian el mundo', desc: 'Tesla, NVIDIA, las que innovan' },
      { value: 'passive_income', label: 'Ingresos pasivos', desc: 'Dividendos que me paguen cada mes' },
      { value: 'balance', label: 'Un poco de todo', desc: 'Balance entre crecimiento y seguridad' },
    ],
  },
  {
    id: 'experience',
    title: 'Tu experiencia',
    question: 'Cuanto sabes de inversiones?',
    options: [
      { value: 'none', label: 'Nada, soy completamente nuevo', desc: 'Quiero aprender desde cero' },
      { value: 'some', label: 'Se lo basico', desc: 'Se que son acciones y ETFs' },
      { value: 'research', label: 'Investigo antes de invertir', desc: 'Leo reportes y analizo' },
    ],
  },
  {
    id: 'sectors',
    title: 'Tus intereses',
    question: 'Que sectores te interesan? (puedes elegir varios)',
    multi: true,
    options: [
      { value: 'tech', label: 'Tecnologia', desc: 'Apple, NVIDIA, Microsoft' },
      { value: 'health', label: 'Salud', desc: 'Farmaceuticas, biotech' },
      { value: 'energy', label: 'Energia', desc: 'Petroleo, renovables' },
      { value: 'finance', label: 'Finanzas', desc: 'Bancos, fintech' },
      { value: 'all', label: 'Todos me interesan', desc: 'Diversificar en todo' },
    ],
  },
  {
    id: 'concentration',
    title: 'Tu estilo',
    question: 'Prefieres pocas acciones que conoces bien, o muchas para diversificar?',
    options: [
      { value: 'concentrated', label: 'Pocas pero buenas', desc: '5-10 acciones que entiendo a fondo' },
      { value: 'diversified', label: 'Muchas para diversificar', desc: 'ETFs y muchas acciones diferentes' },
    ],
  },
];

// Portfolios result display
const PORTFOLIOS = {
  guardian: {
    name: 'Guardian',
    icon: '🛡️',
    color: '#2563EB',
    desc: 'Protege tu capital mientras crece de forma estable',
    risk: 'Conservador',
    expectedReturn: '6-9% anual',
    composition: [
      { symbol: 'BND', pct: 30, color: '#3B82F6' },
      { symbol: 'SCHD', pct: 25, color: '#10B981' },
      { symbol: 'VOO', pct: 20, color: '#2563EB' },
      { symbol: 'GLD', pct: 15, color: '#F59E0B' },
      { symbol: 'VNQ', pct: 10, color: '#8B5CF6' },
    ],
  },
  explorer: {
    name: 'Explorador',
    icon: '🧭',
    color: '#7C3AED',
    desc: 'Balance entre crecimiento y proteccion para crecer con confianza',
    risk: 'Moderado',
    expectedReturn: '9-12% anual',
    composition: [
      { symbol: 'VOO', pct: 35, color: '#2563EB' },
      { symbol: 'QQQ', pct: 20, color: '#7C3AED' },
      { symbol: 'BND', pct: 15, color: '#3B82F6' },
      { symbol: 'VWO', pct: 15, color: '#10B981' },
      { symbol: 'GLD', pct: 10, color: '#F59E0B' },
      { symbol: 'SCHD', pct: 5, color: '#EC4899' },
    ],
  },
  visionary: {
    name: 'Visionario',
    icon: '🚀',
    color: '#DC2626',
    desc: 'Apuesta por las empresas que estan cambiando el mundo',
    risk: 'Agresivo',
    expectedReturn: '12-18% anual',
    composition: [
      { symbol: 'QQQ', pct: 25, color: '#7C3AED' },
      { symbol: 'VOO', pct: 20, color: '#2563EB' },
      { symbol: 'SOXX', pct: 15, color: '#DC2626' },
      { symbol: 'PLTR', pct: 10, color: '#F59E0B' },
      { symbol: 'VWO', pct: 10, color: '#10B981' },
      { symbol: 'ARKK', pct: 10, color: '#EC4899' },
      { symbol: 'HIMS', pct: 5, color: '#8B5CF6' },
      { symbol: 'DUOL', pct: 5, color: '#06B6D4' },
    ],
  },
  income_builder: {
    name: 'Constructor de Ingresos',
    icon: '💰',
    color: '#059669',
    desc: 'Genera dividendos crecientes que trabajan para ti 24/7',
    risk: 'Moderado-Conservador',
    expectedReturn: '7-10% anual + 2.8% dividendos',
    composition: [
      { symbol: 'SCHD', pct: 30, color: '#10B981' },
      { symbol: 'VYM', pct: 20, color: '#059669' },
      { symbol: 'VOO', pct: 20, color: '#2563EB' },
      { symbol: 'BND', pct: 15, color: '#3B82F6' },
      { symbol: 'O', pct: 10, color: '#F59E0B' },
      { symbol: 'KO', pct: 5, color: '#DC2626' },
    ],
  },
};

function StepDots({ total, current }) {
  return (
    <div className="flex gap-2 justify-center">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`rounded-full transition-all duration-300 ${
            i === current ? 'w-8 h-2 bg-[#2A59C6]' : i < current ? 'w-2 h-2 bg-[#2A59C6]' : 'w-2 h-2 bg-[#E5E7EB]'
          }`}
        />
      ))}
    </div>
  );
}

function CompositionBar({ composition }) {
  return (
    <div className="w-full">
      <div className="flex h-3 rounded-full overflow-hidden mb-3">
        {composition.map((item) => (
          <div
            key={item.symbol}
            style={{ width: `${item.pct}%`, backgroundColor: item.color }}
            className="transition-all"
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {composition.map((item) => (
          <div key={item.symbol} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-xs font-semibold text-[#181D27]">{item.symbol}</span>
            <span className="text-xs text-[#9CA3AF]">{item.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InvestorOnboarding({ onComplete }) {
  const snap = useSnapshot(state);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [multiSelected, setMultiSelected] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const question = QUESTIONS[step];
  const isMulti = question?.multi;

  const handleSelect = (value) => {
    if (isMulti) {
      if (value === 'all') {
        setMultiSelected(['all']);
      } else {
        const without = multiSelected.filter(v => v !== 'all');
        if (without.includes(value)) {
          setMultiSelected(without.filter(v => v !== value));
        } else {
          setMultiSelected([...without, value]);
        }
      }
    } else {
      setSelected(value);
    }
  };

  const canProceed = isMulti ? multiSelected.length > 0 : selected !== null;

  const handleNext = async () => {
    if (!canProceed) return;

    const value = isMulti ? multiSelected : selected;
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);
    setSelected(null);
    setMultiSelected([]);

    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      // Submit to backend
      setLoading(true);
      setError('');
      try {
        const response = await completeOnboarding(newAnswers);

        // Auto-provision Alpaca account on first onboarding completion.
        // Account is created with $0 — the user must deposit real funds (USDC → USD)
        // to start investing. Virtual funding ($5K) is reserved for explicit demo
        // invites and is provisioned through a separate admin script, not here.
        let alpacaInfo = null;
        if (snap.user?.firstName && snap.user?.lastName) {
          try {
            const phone = snap.user?.phonenumber?.number
              ? `${snap.user.phonenumber.nationality?.area_code || ''}${snap.user.phonenumber.number}`
              : undefined;
            alpacaInfo = await createAlpacaAccount({
              firstName: snap.user.firstName,
              lastName: snap.user.lastName,
              phoneNumber: phone,
              // No initialFundUsd — real users deposit via USDC, not auto-funded
            });
            if (alpacaInfo?.alpacaAccountId) {
              // Persist to localStorage so getAlpacaAccountId() picks it up
              const stored = JSON.parse(localStorage.getItem('fp_investor_profile') || '{}');
              localStorage.setItem('fp_investor_profile', JSON.stringify({
                ...stored,
                ...response,
                alpacaAccountId: alpacaInfo.alpacaAccountId,
              }));
            }
          } catch (alpacaErr) {
            console.warn('Alpaca provision failed (non-blocking):', alpacaErr.message);
          }
        }

        setResult({ ...response, alpaca: alpacaInfo });
        setShowResult(true);
      } catch (err) {
        // Fallback: calculate locally if backend is offline
        console.warn('Backend offline, calculating locally:', err.message);
        const portfolio = calculateLocalPortfolio(newAnswers);
        setResult({
          portfolio,
          thesis: PORTFOLIOS[portfolio]?.desc || '',
          tier: 'basic',
          level: 1,
        });
        setShowResult(true);
        // Save locally as backup
        localStorage.setItem('fp_investor_profile', JSON.stringify({
          answers: newAnswers,
          portfolio,
          createdAt: new Date().toISOString(),
        }));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (step > 0) {
      const prevQuestion = QUESTIONS[step - 1];
      setStep(step - 1);
      if (prevQuestion.multi) {
        setMultiSelected(answers[prevQuestion.id] || []);
        setSelected(null);
      } else {
        setSelected(answers[prevQuestion.id] || null);
        setMultiSelected([]);
      }
    }
  };

  const handleConfirm = () => {
    onComplete(result);
  };

  // Loading state
  if (loading) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <div className="animate-spin w-10 h-10 border-3 border-[#2A59C6] border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-sm text-[#6B7280]">Analizando tu perfil...</p>
      </div>
    );
  }

  // Result screen
  if (showResult && result) {
    const portfolio = PORTFOLIOS[result.portfolio];
    if (!portfolio) {
      return (
        <div className="max-w-lg mx-auto px-4 py-10 text-center">
          <p className="text-sm text-red-500">Error: portafolio no encontrado</p>
          <button onClick={() => { setShowResult(false); setStep(0); setAnswers({}); }} className="mt-4 text-sm text-[#2A59C6]">Reintentar</button>
        </div>
      );
    }

    return (
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="text-4xl mb-3">{portfolio.icon}</div>
          <h2 className="text-2xl font-bold text-[#181D27] mb-1">Tu portafolio: {portfolio.name}</h2>
          <p className="text-sm text-[#6B7280]">{portfolio.desc}</p>
        </div>

        {/* Thesis */}
        <div className="bg-[#F0F4FF] rounded-2xl p-4 mb-4">
          <div className="flex items-start gap-3">
            <span className="text-lg">🔮</span>
            <div>
              <div className="text-xs font-semibold text-[#2A59C6] mb-1">Tu tesis de inversión (según El Oráculo)</div>
              <p className="text-sm text-[#4B5563] leading-relaxed">{result.thesis}</p>
            </div>
          </div>
        </div>

        {/* Alpaca account status */}
        {result.alpaca?.alpacaAccountId && (
          <div className="bg-[#F0FDF4] border border-[#86EFAC] rounded-2xl p-4 mb-4">
            <div className="flex items-start gap-3">
              <span className="text-lg">🎉</span>
              <div>
                <div className="text-xs font-semibold text-[#059669] mb-1">Cuenta de inversión activada</div>
                <p className="text-sm text-[#14532D] leading-relaxed">
                  Tu cuenta de broker ya está lista. Para empezar a invertir,
                  recarga USDC desde tu billetera FlowexPay y conviértelos a USD —
                  en menos de un minuto verás tu saldo disponible.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-3 text-center">
            <div className="text-xs text-[#9CA3AF] mb-1">Riesgo</div>
            <div className="text-sm font-semibold" style={{ color: portfolio.color }}>{portfolio.risk}</div>
          </div>
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-3 text-center">
            <div className="text-xs text-[#9CA3AF] mb-1">Retorno esperado</div>
            <div className="text-sm font-semibold text-[#181D27]">{portfolio.expectedReturn}</div>
          </div>
        </div>

        {/* Composition */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
          <div className="text-sm font-semibold text-[#181D27] mb-4">Composicion sugerida</div>
          <CompositionBar composition={portfolio.composition} />
        </div>

        {/* Level badge */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#F0F4FF] flex items-center justify-center text-lg">🔍</div>
            <div>
              <div className="text-sm font-semibold text-[#181D27]">Nivel 1: Curioso</div>
              <div className="text-xs text-[#6B7280]">Desbloquea mas analisis invirtiendo y aprendiendo</div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-[#F9FAFB] rounded-xl p-3 mb-6">
          <p className="text-[11px] text-[#9CA3AF] leading-relaxed">
            Rendimientos pasados no garantizan resultados futuros. Este portafolio es una sugerencia basada en tu perfil. Puedes personalizarlo en cualquier momento.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => { setShowResult(false); setStep(0); setAnswers({}); setSelected(null); setMultiSelected([]); }}
            className="flex-1 py-3.5 border border-[#E5E7EB] rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-[#F9FAFB] transition"
          >
            Repetir
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 py-3.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition"
          >
            Comenzar
          </button>
        </div>
      </div>
    );
  }

  // Question flow
  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBack}
          className={`text-sm text-[#6B7280] hover:text-[#2A59C6] transition ${step === 0 ? 'invisible' : ''}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <StepDots total={QUESTIONS.length} current={step} />
        <span className="text-xs text-[#9CA3AF]">{step + 1}/{QUESTIONS.length}</span>
      </div>

      {/* Question */}
      <div className="mb-8">
        <div className="text-xs font-semibold text-[#2A59C6] uppercase tracking-wider mb-2">{question.title}</div>
        <h2 className="text-xl font-bold text-[#181D27]">{question.question}</h2>
        {isMulti && <p className="text-xs text-[#9CA3AF] mt-1">Puedes seleccionar varios</p>}
      </div>

      {/* Options */}
      <div className="space-y-3 mb-8">
        {question.options.map((opt) => {
          const isSelected = isMulti ? multiSelected.includes(opt.value) : selected === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              className={`w-full text-left px-5 py-4 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-[#2A59C6] bg-[#F0F4FF]'
                  : 'border-[#E5E7EB] bg-white hover:border-[#C7D2E8]'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className={`text-sm font-semibold ${isSelected ? 'text-[#2A59C6]' : 'text-[#181D27]'}`}>
                    {opt.label}
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5">{opt.desc}</div>
                </div>
                <div className={`w-5 h-5 rounded-${isMulti ? 'md' : 'full'} border-2 flex items-center justify-center transition ${
                  isSelected ? 'border-[#2A59C6] bg-[#2A59C6]' : 'border-[#D1D5DB]'
                }`}>
                  {isSelected && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && <p className="text-xs text-red-500 mb-3 text-center">{error}</p>}

      {/* Next button */}
      <button
        onClick={handleNext}
        disabled={!canProceed}
        className={`w-full py-3.5 rounded-xl text-sm font-semibold transition ${
          canProceed
            ? 'bg-[#2A59C6] text-white hover:bg-[#1E4BB8]'
            : 'bg-[#E5E7EB] text-[#9CA3AF] cursor-not-allowed'
        }`}
      >
        {step < QUESTIONS.length - 1 ? 'Siguiente' : 'Ver mi portafolio'}
      </button>

      {/* Skip */}
      {step === 0 && (
        <button
          onClick={() => onComplete(null)}
          className="w-full mt-3 py-2 text-xs text-[#9CA3AF] hover:text-[#6B7280] transition"
        >
          Omitir por ahora
        </button>
      )}
    </div>
  );
}

// Local fallback when backend is offline
function calculateLocalPortfolio(answers) {
  if (answers.reactionToDrop === 'sell' || (answers.timeHorizon === 'short' && answers.excitement === 'safe_growth')) {
    return 'guardian';
  }
  if (answers.excitement === 'passive_income') {
    return 'income_builder';
  }
  if (answers.reactionToDrop === 'buy_more' && (answers.timeHorizon === 'long' || answers.timeHorizon === 'unlimited') && answers.excitement === 'world_changers') {
    return 'visionary';
  }
  return 'explorer';
}

export { PORTFOLIOS };
